import { useCallback, useMemo, useRef } from 'react'
import useVirtualization, { VirtualizationTypes } from 'modules/core/hooks/useVirtualization'
import { Box } from '@factorialco/deprecated-design-system'
import { TimeGridRow } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/components/TimeGridRow'
import {
  GridVirtualizationProps,
  TimeGridEntity,
  TimeGridEvent,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

const GridVirtualization = <
  TEntity extends TimeGridEntity,
  TEvent extends TimeGridEvent,
  TDragDrop,
>({
  rows,
  rowMinHeight,
  scrollContainerRef,
  preloadedRowsOutsideViewport,
  enableRowVirtualization,
  gridMinWidth,
  rowHeaderRenderer,
  eventRenderer,
  cardAppearance,
  secondaryLayerRenderer,
  rowActionsRenderer,
  dragDropHandlers,
  dragAndDropAdapter,
  rowHeaderWidth,
  minColumnWidth,
  paddingX,
  paddingY,
  cardLayout,
  showGridLines,
  slots,
  isCellSelectable,
  isCellDisabled,
  onCellSelected,
  selectedCellKeys,
  selectedRowIds,
  selectedColumnIndexes,
  selectedEventKeys,
  isAllSelected,
  onEventSelected,
  onCopySelection,
  onRowHeaderSelected,
  onHeaderDragStart,
  onAddShiftClick,
  ghostCells,
  ghostAnchorCell,
  onGhostCardRef,
  keyboardEndpointRowIndex,
  keyboardScrollMargin,
}: GridVirtualizationProps<TEntity, TEvent, TDragDrop>) => {
  const measuredHeightsRef = useRef<Map<string, number>>(new Map())
  const disabledSlotsCacheRef = useRef<Map<string, { entity: TEntity; indexes: Set<number> }>>(
    new Map()
  )
  const disabledSlotsCacheDepsRef = useRef<{
    slots: GridVirtualizationProps<TEntity, TEvent, TDragDrop>['slots']
    isCellDisabled: GridVirtualizationProps<TEntity, TEvent, TDragDrop>['isCellDisabled']
  } | null>(null)
  const rowNodesRef = useRef<Map<number, HTMLDivElement>>(new Map())

  if (
    disabledSlotsCacheDepsRef.current?.slots !== slots ||
    disabledSlotsCacheDepsRef.current?.isCellDisabled !== isCellDisabled
  ) {
    disabledSlotsCacheRef.current.clear()
    disabledSlotsCacheDepsRef.current = { slots, isCellDisabled }
  }

  const setRowHeightRef = useCallback(
    (rowIndex: number, rowId: string, node: HTMLDivElement | null) => {
      if (!node) {
        rowNodesRef.current.delete(rowIndex)
        return
      }
      rowNodesRef.current.set(rowIndex, node)
      const measuredHeight = node.clientHeight
      if (measuredHeight > 0) {
        measuredHeightsRef.current.set(rowId, measuredHeight)
      }
    },
    []
  )
  const getRowHeight = (index: number): number => {
    const row = rows[index]
    if (!row) return rowMinHeight

    return measuredHeightsRef.current.get(row.entity.id) ?? rowMinHeight
  }

  const { visibleStartIndex, endIndex, topPlaceholderHeight, bottomPlaceholderHeight } =
    useVirtualization({
      items: rows,
      defaultItemHeight: rowMinHeight,
      getHeight: getRowHeight,
      containerRef: scrollContainerRef,
      type: VirtualizationTypes.VARIABLE,
      preloadedElementsOutsideViewport: preloadedRowsOutsideViewport,
    })
  const visibleRows = enableRowVirtualization ? rows.slice(visibleStartIndex, endIndex) : rows

  const gridStyle: Partial<CSSStyleDeclaration> = {
    minWidth: `${gridMinWidth}px`,
  }
  const isSingleSelectedEventGlobally = selectedEventKeys.size === 1
  const selectedEventIdsByEntity = useMemo(() => {
    const eventsByEntity = new Map<string, Set<string>>()

    for (const key of selectedEventKeys) {
      const separatorIndex = key.indexOf(':')
      if (separatorIndex === -1) continue

      const entityId = key.slice(0, separatorIndex)
      const eventId = key.slice(separatorIndex + 1)
      const eventIds = eventsByEntity.get(entityId)

      if (eventIds) {
        eventIds.add(eventId)
      } else {
        eventsByEntity.set(entityId, new Set([eventId]))
      }
    }

    return eventsByEntity
  }, [selectedEventKeys])
  const ghostSlotsByEntity = useMemo(() => {
    const slotsByEntity = new Map<string, Set<number>>()

    ghostCells?.forEach(({ entityId, slotIndex }) => {
      const slotIndexes = slotsByEntity.get(entityId)

      if (slotIndexes) {
        slotIndexes.add(slotIndex)
      } else {
        slotsByEntity.set(entityId, new Set([slotIndex]))
      }
    })

    return slotsByEntity
  }, [ghostCells])

  useUnsafeEffect(() => {
    if (keyboardEndpointRowIndex == null) return

    const rowNode = rowNodesRef.current.get(keyboardEndpointRowIndex)
    if (!rowNode) return

    rowNode.style.scrollMarginTop = keyboardScrollMargin ? `${keyboardScrollMargin.top}px` : ''
    rowNode.style.scrollMarginBottom = keyboardScrollMargin
      ? `${keyboardScrollMargin.bottom}px`
      : ''
    rowNode.scrollIntoView({ block: 'nearest', behavior: 'smooth', inline: 'nearest' })
  }, [keyboardEndpointRowIndex])

  return (
    <Box
      role="rowgroup"
      data-testid="time-grid-rowgroup"
      data-grid-width={gridMinWidth}
      style={gridStyle}
    >
      {enableRowVirtualization ? (
        <Box
          aria-hidden="true"
          style={{ height: `${topPlaceholderHeight}px`, minWidth: `${gridMinWidth}px` }}
        />
      ) : null}
      {visibleRows.map((row, visibleRowIndex) => {
        const rowIndex = enableRowVirtualization
          ? visibleStartIndex + visibleRowIndex
          : visibleRowIndex
        const selectedEventIds = selectedEventIdsByEntity.get(row.entity.id)
        const ghostCellSlotIndexes = ghostSlotsByEntity.get(row.entity.id) ?? null
        const cachedDisabledSlots = disabledSlotsCacheRef.current.get(row.entity.id)
        const disabledCellSlotIndexes = !isCellDisabled
          ? undefined
          : cachedDisabledSlots?.entity === row.entity
            ? cachedDisabledSlots.indexes
            : slots.reduce<Set<number>>((indexes, slot, slotIndex) => {
                if (isCellDisabled(row.entity, slot)) {
                  indexes.add(slotIndex)
                }

                return indexes
              }, new Set())

        if (disabledCellSlotIndexes && cachedDisabledSlots?.entity !== row.entity) {
          disabledSlotsCacheRef.current.set(row.entity.id, {
            entity: row.entity,
            indexes: disabledCellSlotIndexes,
          })
        }

        const isRowSelectable = row.entity.selectable !== false
        const isRowSelected =
          (isAllSelected && isRowSelectable) || (selectedRowIds?.has(row.entity.id) ?? false)
        let selectableSlotIndexes: Set<number> | null = null
        const getSelectableSlotIndexes = () => {
          if (selectableSlotIndexes) return selectableSlotIndexes

          selectableSlotIndexes = new Set(
            slots.flatMap((slot, index) =>
              !isCellSelectable || isCellSelectable(row.entity, slot) ? [index] : []
            )
          )
          return selectableSlotIndexes
        }
        const selectedCellSlots = isRowSelected
          ? getSelectableSlotIndexes()
          : selectedColumnIndexes !== null && selectedColumnIndexes.size > 0
            ? new Set(
                Array.from(selectedColumnIndexes).filter((index) =>
                  getSelectableSlotIndexes().has(index)
                )
              )
            : new Set(
                Array.from(selectedCellKeys)
                  .filter((key) => key.startsWith(`${row.entity.id}:`))
                  .map((key) => Number(key.split(':')[1]))
              )

        return (
          <TimeGridRow
            key={row.entity.id}
            entity={row.entity}
            rowHeaderWidth={rowHeaderWidth}
            minColumnWidth={minColumnWidth}
            gridMinWidth={gridMinWidth}
            paddingX={paddingX}
            paddingY={paddingY}
            cardLayout={cardLayout}
            showGridLines={showGridLines}
            columnCount={slots.length}
            slots={slots}
            layouts={row.layouts}
            rowHeader={rowHeaderRenderer(row.entity, {
              rowHeight: rowMinHeight,
              dragDropHandlers,
            })}
            rowActions={rowActionsRenderer?.(row.entity, {
              rowHeight: rowMinHeight,
              dragDropHandlers,
            })}
            eventRenderer={eventRenderer}
            cardAppearance={cardAppearance}
            dragDropHandlers={dragDropHandlers}
            dragAndDropAdapter={dragAndDropAdapter}
            secondaryLayer={secondaryLayerRenderer?.({ entity: row.entity, slots })}
            rowRef={(node) => setRowHeightRef(rowIndex, row.entity.id, node)}
            selectedCellSlots={selectedCellSlots}
            isCellSelectable={
              isCellSelectable
                ? (slotIndex) => {
                    const slot = slots[slotIndex]
                    return slot ? isCellSelectable(row.entity, slot) : false
                  }
                : undefined
            }
            disabledCellSlotIndexes={disabledCellSlotIndexes}
            onCellSelected={(slotIndex, modifiers) =>
              onCellSelected(row.entity, slotIndex, modifiers)
            }
            isRowSelected={isRowSelected}
            onRowHeaderSelected={(modifiers) => onRowHeaderSelected(row.entity, modifiers)}
            onHeaderDragStart={onHeaderDragStart}
            rowIndex={rowIndex}
            selectedEventIds={selectedEventIds}
            isSingleSelectedEventGlobally={isSingleSelectedEventGlobally}
            onEventSelected={(eventId, modifiers) =>
              onEventSelected?.(row.entity.id, eventId, modifiers)
            }
            onCopySelection={onCopySelection}
            onAddShiftClick={
              onAddShiftClick && row.entity.selectable !== false
                ? (slotIndex, anchorRect) => onAddShiftClick(row.entity, slotIndex, anchorRect)
                : undefined
            }
            ghostCellSlotIndexes={ghostCellSlotIndexes}
            ghostAnchorSlotIndex={
              ghostAnchorCell?.entityId === row.entity.id ? ghostAnchorCell.slotIndex : null
            }
            onGhostCardRef={
              ghostAnchorCell?.entityId === row.entity.id ? onGhostCardRef : undefined
            }
          />
        )
      })}
      {enableRowVirtualization ? (
        <Box
          aria-hidden="true"
          style={{ height: `${bottomPlaceholderHeight}px`, minWidth: `${gridMinWidth}px` }}
        />
      ) : null}
    </Box>
  )
}

export { GridVirtualization }
